import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Cookies, withCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { isDesktop } from 'react-device-detect';
import { ThemeContext } from 'styled-components';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PolicyContent from '../PolicyContent';
import Api from '../../../../../../api/LeadGenService/v1';
import Patterns from '../../../../../../utils/pattern/patterns';
import { ERROR, SUBSCRIBED } from '../../utils/constants';
import { CallToAction } from '../../../sharedComponents';
import { TextField } from '../TextField';
import { StyledForm, StyledCallToAction, Text } from '../../styles';
import { getStateFieldLabel } from '../../../../../paymentJourney/DelegateDetailsForm/sharedComponents/DistanceLearningForm';
import { Select } from '../Select';
import { CheckboxField } from '../CheckboxField';
import {
  countriesList,
  statesList,
} from '../../../../../../services/Dictionary';
import { useRootSelector } from '../../../../../../store/hooks';
import { selectSiteHeader } from '../../../../../../store/features/siteHeader';
import { FormMessage } from '../../../../../shared/forms';

import {
  InterestsWrapper,
  ExpandButton,
  CheckboxGroupWrapper,
} from './index.style';
import gtmService from '../../../../../../utils/gtm';

const EmailOptInForm = (props) => {
  const {
    cookieName,
    cookies,
    sapProductCode,
    buttonText,
    interestTitle,
    userInterests = [],
    setSubscriptionStatus,
    pageConfig: { cookieSitePath },
    moduleType,
  } = props;

  const themeConfig = useContext(ThemeContext);
  const dStyles = themeConfig.module.dStyles;
  const vStyles = themeConfig.module.vStyles;
  const { data: { nameFormat } = {} } = useRootSelector(selectSiteHeader);

  const {
    elements: { callToAction: { settings: { size: ctaSize } = {} } = {} } = {},
  } = themeConfig.module.dStyles.elements.form;

  const { i18n, t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [interestsError, setInterestsError] = useState(false);
  const [isExpanded, setExpanded] = useState(true);

  const methods = useForm({
    mode: 'onSubmit',
  });

  const {
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = methods;

  const selectedCountry = watch('country');

  // Collect selected interest codes
  const getInterestCodes = (formValues) =>
    userInterests
      .filter((interest) => formValues[`codes-${interest.code}`])
      .map((interest) => interest.code);

  const handleExpand = () => {
    if (!isDesktop) {
      setExpanded(!isExpanded);
    }
  };

  const submitHandler = async function (values) {
    const title = props.headingText;
    // To prevent sending an Email Opt In, we check for filled-up field, as this indicates that the request may not have been sent by a human.
    if (values.company) return;

    const userInterestCodes = getInterestCodes(values);

    if (userInterests.length && !userInterestCodes.length) {
      setInterestsError(true);
      return;
    } else {
      setInterestsError(false);
    }

    setLoading(true);

    const response = await fetch(Api.subscribe('email/event'), {
      method: 'POST',
      headers: {
        'Accept-Language': i18n.language,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...values,
        productCode: sapProductCode,
        userInterestCodes: getInterestCodes(values),
      }),
    });
    setLoading(false);

    if (response.status !== 200) {
      setSubscriptionStatus(ERROR);
      return;
    }

    gtmService.ga4GenerateLead({
      event_label: `User subscribes through event email opt in form`,
      form_id: 'email_opt_in_form_id',
      form_title: title,
    });

    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 7);

    const acceptedSAPCodes = cookies.get(cookieName) || [];
    const updatedAcceptedSAPCodes = [...acceptedSAPCodes, sapProductCode];

    cookies.set(cookieName, JSON.stringify(updatedAcceptedSAPCodes), {
      path: cookieSitePath,
      expires: expirationDate,
      secure: true,
    });
    setSubscriptionStatus(SUBSCRIBED);
  };

  const countriesOptions = Object.keys(countriesList).map((key) => {
    return {
      value: key,
      label: t(countriesList[key]),
    };
  });

  const statesOptions =
    statesList[selectedCountry] &&
    Object.keys(statesList[selectedCountry]).map((key) => {
      return {
        value: key,
        label: t(statesList[selectedCountry][key]),
      };
    });

  const firstNamePlaceholder = t(
    `global.name-format.${nameFormat}.firstname.label`,
  );
  const lastNamePlaceholder = t(
    `global.name-format.${nameFormat}.lastname.label`,
  );

  return (
    <FormProvider {...methods}>
      <StyledForm
        name="emailOptInForm"
        role="form"
        onSubmit={handleSubmit(submitHandler)}
        noValidate
        data-testid="email-opt-in-form"
      >
        <div className="row">
          <div className={'col-xs-12 col-md-6'}>
            <TextField
              type="text"
              name="firstName"
              placeholder={`${firstNamePlaceholder} *`}
              rules={{
                required: true,
              }}
              hasError={!!errors?.firstName}
            />
          </div>
          <div className={'col-xs-12 col-md-6'}>
            <TextField
              type="text"
              name="lastName"
              placeholder={`${lastNamePlaceholder} *`}
              rules={{
                required: true,
              }}
              hasError={!!errors?.lastName}
            />
          </div>
        </div>
        <div className="row">
          <div className={'col-xs-12 col-md-6'}>
            <TextField
              type="email"
              name="email"
              placeholder={t('site.email-opt-in.email.placeholder')}
              rules={{
                required: true,
                validate: (value) => Patterns.isEmail(value),
              }}
              hasError={!!errors?.email}
            />
          </div>
          <div className={'col-xs-12 col-md-6'}>
            <Select
              name="country"
              options={countriesOptions}
              placeholder={t('site.email-opt-in.country.placeholder')}
              rules={{
                required: true,
              }}
              hasError={!!errors?.country}
              testId="email-ept-in-country-select"
            />
            {statesList[selectedCountry] && (
              <Select
                name="state"
                options={statesOptions}
                placeholder={t(getStateFieldLabel(selectedCountry))}
                rules={{
                  required: true,
                }}
                hasError={!!errors?.state}
                testId="email-ept-in-state-select"
              />
            )}
            <div hidden>
              <TextField type="text" name="company" placeholder="Company" />
            </div>
          </div>
        </div>
        {userInterests.length > 0 && (
          <InterestsWrapper>
            <div className="row">
              <div className="col-xs-10">
                {interestTitle && (
                  <Text
                    visualStyles={vStyles}
                    displayStyles={dStyles}
                    onClick={handleExpand}
                  >
                    {interestTitle}
                  </Text>
                )}
              </div>
              {!isDesktop && (
                <div className="col-xs-2">
                  <ExpandButton
                    className="pull-right"
                    onClick={handleExpand}
                    isExpanded={isExpanded}
                  >
                    <ExpandMoreIcon />
                  </ExpandButton>
                </div>
              )}
            </div>
            {isExpanded && (
              <CheckboxGroupWrapper isExpanded={isExpanded}>
                <div className="row">
                  {userInterests.map((userInterest, index) => (
                    <div
                      className="col-xs-12 col-md-6"
                      key={`${index}-${userInterest.code}`}
                    >
                      <CheckboxField
                        key={index}
                        name={`codes-${userInterest.code}`}
                        label={userInterest.interestName}
                        testId={`email-opt-in-subsection-${userInterest.code}`}
                      />
                    </div>
                  ))}
                </div>
              </CheckboxGroupWrapper>
            )}

            <FormMessage type="danger" enabled={interestsError}>
              <p>{t('site.email-opt-in.error.interests')}</p>
            </FormMessage>
          </InterestsWrapper>
        )}
        {isDirty && <PolicyContent buttonText={buttonText} />}
        <StyledCallToAction>
          <CallToAction
            type="submit"
            size={ctaSize}
            mode={themeConfig.name === 'FanExpo' ? 'white' : 'none'}
            label={buttonText}
            loading={loading}
            ga4={{
              module_type: moduleType,
            }}
            className="c-email-opt-in-submit"
            testId="cta-submit-subscription"
          />
        </StyledCallToAction>
      </StyledForm>
    </FormProvider>
  );
};

EmailOptInForm.propTypes = {
  headingText: PropTypes.string,
  cookieName: PropTypes.string,
  sapProductCode: PropTypes.string,
  buttonText: PropTypes.string,
  setSubscriptionStatus: PropTypes.func,
  interestTitle: PropTypes.string,
  moduleType: PropTypes.string,
  userInterests: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      interestTitle: PropTypes.string,
    }),
  ),
  pageConfig: PropTypes.shape({
    cookieSitePath: PropTypes.string,
  }),
  cookies: PropTypes.instanceOf(Cookies),
};

const mapStateToProps = (state) => ({
  pageConfig: state.pageConfig,
});

export default connect(mapStateToProps)(withCookies(EmailOptInForm));
